import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { checkAuth } from '../api/auth';

const STORE_KEY = 'ddiring_selected_store';
const CHANNEL_KEY = 'ddiring_selected_channel';
const DEVICE_KEY = 'ddiring_selected_device';
const VIRTUAL_NUMBER_KEY = 'ddiring_virtual_number';

const loadFromStorage = (key) => {
    try {
        const saved = localStorage.getItem(key);
        return saved ? JSON.parse(saved) : null;
    } catch {
        return null;
    }
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isAuthChecking, setIsAuthChecking] = useState(true);
    const [selectedStore, setSelectedStore] = useState(() => loadFromStorage(STORE_KEY));
    const [selectedChannel, setSelectedChannel] = useState(() => loadFromStorage(CHANNEL_KEY));
    const [selectedDevice, setSelectedDevice] = useState(() => loadFromStorage(DEVICE_KEY));
    const [virtualNumber, setVirtualNumber] = useState(() => loadFromStorage(VIRTUAL_NUMBER_KEY));

    const selectStore = (store) => {
        localStorage.setItem(STORE_KEY, JSON.stringify(store));
        localStorage.removeItem(CHANNEL_KEY);
        localStorage.removeItem(DEVICE_KEY);
        localStorage.removeItem(VIRTUAL_NUMBER_KEY);
        setSelectedStore(store);
        setSelectedChannel(null);
        setSelectedDevice(null);
        setVirtualNumber(null);
    };

    const selectChannel = (channel) => {
        localStorage.setItem(CHANNEL_KEY, JSON.stringify(channel));
        setSelectedChannel(channel);
    };

    const selectDevice = (device) => {
        localStorage.setItem(DEVICE_KEY, JSON.stringify(device));
        setSelectedDevice(device);
    };

    const saveVirtualNumber = (number) => {
        localStorage.setItem(VIRTUAL_NUMBER_KEY, JSON.stringify(number));
        setVirtualNumber(number);
    };

    const clearOnboarding = useCallback(() => {
        localStorage.removeItem(STORE_KEY);
        localStorage.removeItem(CHANNEL_KEY);
        localStorage.removeItem(DEVICE_KEY);
        localStorage.removeItem(VIRTUAL_NUMBER_KEY);
        setSelectedStore(null);
        setSelectedChannel(null);
        setSelectedDevice(null);
        setVirtualNumber(null);
    }, []);

    const refreshAuth = useCallback(async () => {
        try {
            setIsAuthChecking(true);
            const data = await checkAuth();
            if (data && data.success) {
                setIsLoggedIn(true);
                return true;
            }
            setIsLoggedIn(false);
            clearOnboarding();
            return false;
        } catch (err) {
            setIsLoggedIn(false);
            clearOnboarding();
            return false;
        } finally {
            setIsAuthChecking(false);
        }
    }, [clearOnboarding]);

    useEffect(() => {
        refreshAuth();
    }, [refreshAuth]);

    return (
        <AuthContext.Provider
            value={{
                isLoggedIn,
                setIsLoggedIn,
                isAuthChecking,
                refreshAuth,
                selectedStore,
                selectedChannel,
                selectedDevice,
                virtualNumber,
                selectStore,
                selectChannel,
                selectDevice,
                saveVirtualNumber,
                clearOnboarding,
            }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);